import React from 'react';
import { Box, TextField, FormControlLabel, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SelectInput } from '../inputs';
import { enumToOptions } from '../../utils/misc';
import { IMAPSource, SMTPSource } from '../../models/email-service';
import TextInput from '../inputs/textInput';

interface EmailFormFieldsProps {
  form: {
    imapHost?: string | number;
    imapPort?: string | number;
    imapUsername?: string;
    imapPassword?: string | null;
    imapSource?: string | IMAPSource;
    smtpHost?: string;
    smtpPort?: string | number;
    smtpSecure?: boolean;
    smtpSource?: string | SMTPSource;
    awsSesAccessKeyId?: string;
    awsSesAccessKeySecret?: string | null;
    awsSesLimitPerSecond?: string | number;
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  variant?: 'textfield' | 'textinput';
}

/**
 * Shared email form fields component for IMAP and SMTP configuration.
 * Used by both ProfileDialog and tenantSettingsModal to avoid code duplication.
 */
const EmailFormFields: React.FC<EmailFormFieldsProps> = ({ form, onChange, variant = 'textfield' }) => {
  const { t } = useTranslation();
  
  const imapSourceOptions = enumToOptions(IMAPSource);
  const smtpSourceOptions = enumToOptions(SMTPSource);

  const InputComponent = variant === 'textinput' ? TextInput : TextField;

  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
        {t('IMAP Settings')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        <InputComponent
          fullWidth
          label={t('IMAP Host')}
          name="imapHost"
          value={form.imapHost || ''}
          onChange={onChange}
        />
        <InputComponent
          fullWidth
          type="number"
          label={t('IMAP Port')}
          name="imapPort"
          value={form.imapPort || ''}
          onChange={onChange}
        />
        <InputComponent
          fullWidth
          label={t('IMAP Username')}
          name="imapUsername"
          value={form.imapUsername || ''}
          onChange={onChange}
        />
        <InputComponent
          fullWidth
          type="password"
          label={t('IMAP Password')}
          name="imapPassword"
          value={form.imapPassword === null ? '' : (form.imapPassword || '')}
          onChange={onChange}
          placeholder={form.imapPassword === null ? t('Configured') : undefined}
          slotProps={form.imapPassword === null ? { inputLabel: { shrink: true } } : undefined}
        />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mt: 3 }}>
        <SelectInput
          fullWidth
          label={t('IMAP Source')}
          name="imapSource"
          value={String(form.imapSource || '')}
          onChange={onChange}
          options={imapSourceOptions}
        />
      </Box>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
        {t('SMTP Settings')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        <InputComponent
          fullWidth
          label={t('SMTP Host')}
          name="smtpHost"
          value={form.smtpHost || ''}
          onChange={onChange}
        />
        <InputComponent
          fullWidth
          type="number"
          label={t('SMTP Port')}
          name="smtpPort"
          value={form.smtpPort || ''}
          onChange={onChange}
        />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mt: 3 }}>
        <SelectInput
          fullWidth
          label={t('SMTP Source')}
          name="smtpSource"
          value={String(form.smtpSource || '')}
          onChange={onChange}
          options={smtpSourceOptions}
        />
        <FormControlLabel
          control={
            <Switch
              name="smtpSecure"
              checked={form.smtpSecure || false}
              onChange={onChange}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#212121',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#212121',
                  },
                },
              }}
            />
          }
          label={t('SMTP Secure')}
        />
      </Box>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
        {t('AWS SES Settings')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        <InputComponent
          fullWidth
          label={t('AWS SES Access Key ID')}
          name="awsSesAccessKeyId"
          value={form.awsSesAccessKeyId || ''}
          onChange={onChange}
        />
        <InputComponent
          fullWidth
          type="password"
          label={t('AWS SES Access Key Secret')}
          name="awsSesAccessKeySecret"
          value={form.awsSesAccessKeySecret === null ? '' : (form.awsSesAccessKeySecret || '')}
          onChange={onChange}
          placeholder={form.awsSesAccessKeySecret === null ? t('Configured') : undefined}
          slotProps={form.awsSesAccessKeySecret === null ? { inputLabel: { shrink: true } } : undefined}
        />
        <InputComponent
          fullWidth
          type="number"
          label={t('AWS SES Limit Per Second')}
          name="awsSesLimitPerSecond"
          value={form.awsSesLimitPerSecond || ''}
          onChange={onChange}
        />
      </Box>
    </>
  );
};

export default EmailFormFields;
